import { memo, useCallback, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

import { useAnnotationsToggle } from '~/components/AnnotationsV2/hooks/useAnnotationsToggle';
import {
  useAnnotationV2Store,
  useAnnotationV2TemporalStore,
} from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { useClearAnnotationOnMouseDownOutside } from '~/components/AnnotationsV2/shared/useClearAnnotationOnMouseDownOutside';
import { useFullscreenContext } from '~/components/AssetModal/shared/context/FullscreenContext';
import { useAssetModalRightPanel } from '~/components/AssetModalV2/AssetModalRightPanelContextProvider';
import { stopEscKeyPropagationOnAssetDetailsModal } from '~/components/AssetModalV2/hooks/useAssetModalEscKeyHandler';
import { QueryParamNames } from '~/constants/search';
import { isViewingAnnotatedCommentSelector } from '~/store/router/selectors';
import { setQueryParams } from '~/utils/PathUtils';

export type AnnotationsKeyShortcutsProps = {
  enabled: boolean;
};

export const AnnotationsKeyShortcuts = memo(({ enabled }: AnnotationsKeyShortcutsProps) => {
  const hasActiveDiscussion = useSelector(isViewingAnnotatedCommentSelector);
  const prevHasActiveDiscussion = usePrevious(hasActiveDiscussion);

  const { isFullscreen } = useFullscreenContext();
  const isAnnotatingEnabled = enabled && !isFullscreen;
  const { isAnnotationsEnabled, showAnnotations, hideAnnotations } = useAnnotationsToggle();
  const setAnnotationType = useAnnotationV2Store((state) => state.setAnnotationType);
  const setSelectedTool = useAnnotationV2Store((state) => state.setSelectedTool);
  const undo = useAnnotationV2TemporalStore((state) => state.undo);
  const redo = useAnnotationV2TemporalStore((state) => state.redo);
  const focusCommentInput = useAnnotationV2Store((state) => state.focusCommentInput);
  const { isRightPanelTabOpen, showRightPanelTab, closeRightPanel } = useAssetModalRightPanel();

  const handleToggleCommentsPanel = useCallback(() => {
    if (isRightPanelTabOpen('comments')) {
      closeRightPanel();
    } else {
      showRightPanelTab('comments');
    }
  }, [isRightPanelTabOpen, closeRightPanel, showRightPanelTab]);

  const focusPanelCommentInput = useCallback(() => {
    showRightPanelTab('comments');
    focusCommentInput('panel');
  }, [focusCommentInput, showRightPanelTab]);

  const enablePointAnnotationMode = useCallback(() => {
    setAnnotationType('box');
    setSelectedTool('point');
    showAnnotations();
  }, [setAnnotationType, setSelectedTool, showAnnotations]);

  const toggleAnnotationMode = useCallback(() => {
    if (isAnnotationsEnabled) {
      hideAnnotations();
      return;
    }

    enablePointAnnotationMode();
    showRightPanelTab('comments');
  }, [enablePointAnnotationMode, hideAnnotations, isAnnotationsEnabled, showRightPanelTab]);

  useClearAnnotationOnMouseDownOutside();

  useEffect(() => {
    if (prevHasActiveDiscussion !== hasActiveDiscussion) {
      stopEscKeyPropagationOnAssetDetailsModal(hasActiveDiscussion);
    }
  }, [hasActiveDiscussion, prevHasActiveDiscussion]);

  useHotkeys(
    'esc',
    () => {
      setQueryParams({ [QueryParamNames.discussionId]: null }, 'replace');
    },
    {
      enabled: hasActiveDiscussion,
    },
  );

  useHotkeys(
    'meta+shift+c,ctrl+shift+c',
    () => {
      handleToggleCommentsPanel();
    },
    {
      enabled: !isFullscreen,
      enableOnFormTags: ['TEXTAREA'],
      preventDefault: true,
    },
    [handleToggleCommentsPanel],
  );

  useHotkeys(
    'c',
    (keyboardEvent) => {
      if (keyboardEvent.metaKey || keyboardEvent.ctrlKey || keyboardEvent.altKey || keyboardEvent.shiftKey) {
        return;
      }

      focusPanelCommentInput();
    },
    {
      enabled: isAnnotatingEnabled,
      preventDefault: true,
    },
    [focusPanelCommentInput],
  );

  useHotkeys(
    'p',
    (keyboardEvent) => {
      if (keyboardEvent.metaKey || keyboardEvent.ctrlKey || keyboardEvent.altKey || keyboardEvent.shiftKey) {
        return;
      }

      toggleAnnotationMode();
    },
    {
      enabled: isAnnotatingEnabled,
      preventDefault: true,
    },
    [toggleAnnotationMode],
  );

  useHotkeys(
    'meta+shift+d,ctrl+shift+d',
    () => {
      setAnnotationType('pencil');
      setSelectedTool('pencil');
      showAnnotations();
    },
    {
      enabled: isAnnotatingEnabled,
      enableOnFormTags: ['TEXTAREA'],
      preventDefault: true,
    },
    [setAnnotationType, setSelectedTool, showAnnotations],
  );

  useHotkeys(
    'meta+z,ctrl+z',
    () => undo(),
    {
      enabled: isAnnotatingEnabled,
      enableOnFormTags: ['TEXTAREA'],
      preventDefault: true,
    },
    [undo],
  );

  useHotkeys(
    'meta+shift+z,ctrl+shift+z',
    () => redo(),
    {
      enabled: isAnnotatingEnabled,
      enableOnFormTags: ['TEXTAREA'],
      preventDefault: true,
    },
    [redo],
  );

  return null;
});

AnnotationsKeyShortcuts.displayName = 'AnnotationsKeyShortcuts';
